// src/components/holaveciLocal/DocumentosLocal.js
//
// D-23 — Documentos de validación (D-18) del negocio solo HolaVeci Local:
// cédula del representante (frente y reverso) y RUT. Foto o PDF.
// Muestra el estado de cada uno (pendiente / aprobado / rechazado con
// motivo) y permite subir o reemplazar.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Stack, Typography, Button, Chip, Alert, CircularProgress, Paper,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { documentosLocal } from '../../api/holaveciLocalApi';

const DOCUMENTOS = [
  { tipo: 'CEDULA_FRENTE',  titulo: 'Cédula — frente',  ayuda: 'Foto nítida del frente de la cédula del representante.' },
  { tipo: 'CEDULA_REVERSO', titulo: 'Cédula — reverso', ayuda: 'Foto nítida del reverso de la misma cédula.' },
  { tipo: 'RUT',            titulo: 'RUT',              ayuda: 'PDF o foto del RUT vigente del negocio.' },
];

const EXTENSIONES = '.jpg,.jpeg,.png,.webp,.pdf';
const MAX_BYTES = 8 * 1024 * 1024;

const chipEstado = (doc) => {
  if (!doc) return <Chip size="small" label="Sin subir" variant="outlined" />;
  const e = doc.estadoValidacion || doc.EstadoValidacion;
  if (e === 'APROBADO')  return <Chip size="small" color="success" label="Aprobado" />;
  if (e === 'RECHAZADO') return <Chip size="small" color="error" label="Rechazado" />;
  return <Chip size="small" color="info" label="En revisión" />;
};

export default function DocumentosLocal({ empresaId, onCambio }) {
  const [docs, setDocs]         = useState([]);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(null);
  const [error, setError]       = useState('');
  const inputs = useRef({});

  const cargar = useCallback(async () => {
    try {
      const r = await documentosLocal.misDocumentos(empresaId);
      setDocs(r.documentos || r.docs || []);
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar tus documentos.');
    } finally { setCargando(false); }
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const subir = async (tipo, archivo) => {
    setError('');
    if (!archivo) return;
    if (archivo.size > MAX_BYTES) { setError('El archivo debe pesar máximo 8 MB.'); return; }
    setSubiendo(tipo);
    try {
      await documentosLocal.subir(empresaId, tipo, archivo);
      await cargar();
      onCambio?.();
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudo subir el documento.');
    } finally {
      setSubiendo(null);
      if (inputs.current[tipo]) inputs.current[tipo].value = '';
    }
  };

  if (cargando) {
    return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Stack spacing={1.5}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {DOCUMENTOS.map(({ tipo, titulo, ayuda }) => {
        const doc = docs.find(d => (d.tipo || d.Tipo) === tipo);
        const estado = doc?.estadoValidacion || doc?.EstadoValidacion;
        const motivo = doc?.motivoRechazo || doc?.MotivoRechazo;
        const puedeSubir = !doc || estado === 'RECHAZADO';
        return (
          <Paper key={tipo} variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
                   alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight={700}>{titulo}</Typography>
                  {chipEstado(doc)}
                </Stack>
                <Typography variant="body2" color="text.secondary">{ayuda}</Typography>
                {estado === 'RECHAZADO' && motivo && (
                  <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                    Motivo: {motivo}
                  </Typography>
                )}
              </Box>
              {puedeSubir && (
                <>
                  <input
                    ref={el => { inputs.current[tipo] = el; }}
                    type="file" accept={EXTENSIONES} hidden
                    onChange={(e) => subir(tipo, e.target.files?.[0])}
                  />
                  <Button
                    variant={doc ? 'contained' : 'outlined'}
                    color={doc ? 'warning' : 'primary'}
                    startIcon={subiendo === tipo ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                    disabled={subiendo !== null}
                    onClick={() => inputs.current[tipo]?.click()}
                    sx={{ flexShrink: 0 }}
                  >
                    {subiendo === tipo ? 'Subiendo…' : doc ? 'Subir de nuevo' : 'Subir'}
                  </Button>
                </>
              )}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
